import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, ExternalLink, Maximize2, Minimize2, Loader2 } from 'lucide-react'
import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { appConfig } from '../config/apps.config'

export default function AppView() {
  const { appId } = useParams()
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const app = appConfig[appId]

  // Reset loading state when appId changes
  useEffect(() => {
    setIsLoading(true)
  }, [appId])

  if (!app) {
    return (
      <div className="flex-1 flex items-center justify-center bg-hs-bg">
        <div className="neu-panel p-12 text-center max-w-md">
          <h1 className="text-3xl font-display font-bold text-white mb-4">APP NOT FOUND</h1>
          <p className="text-hs-text mb-8">The requested application does not exist or is currently unavailable.</p>
          <Link to="/dashboard" className="neu-button px-8 py-3 inline-flex items-center text-xs font-mono font-bold tracking-widest text-white uppercase">
            <ArrowLeft size={14} className="mr-2" />
            Return to Dashboard
          </Link>
        </div>
      </div>
    )
  }

  const isDev = import.meta.env.DEV
  const iframeUrl = isDev ? app.devUrl : app.url

  return (
    <div className={`flex flex-col ${isFullscreen ? 'fixed inset-0 z-[100] bg-black' : 'flex-1 min-h-0'}`}>
      <Helmet>
        <title>{app.seo.title}</title>
        <meta name="description" content={app.seo.description} />
        <link rel="canonical" href={`https://headstate.studio/app/${appId}`} />
      </Helmet>

      {/* App Header - Glossy & Compact */}
      <div className="bg-hs-surface/80 backdrop-blur-xl border-b border-white/5 px-6 flex items-center justify-between h-14 relative z-20 shadow-lg">
        <div className="flex items-center space-x-6">
          <Link
            to="/dashboard"
            className="text-hs-text/70 hover:text-white transition-colors flex items-center text-[10px] font-mono font-bold tracking-[0.2em] uppercase"
          >
            <ArrowLeft size={14} className="mr-2" />
            BACK
          </Link>
          <div className="h-6 w-px bg-white/10" />
          <span className="text-white font-display font-bold tracking-widest text-sm text-gloss">{app.name}</span>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="w-9 h-9 neu-button flex items-center justify-center text-hs-text hover:text-white"
            title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
          >
            {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
          <a
            href={app.url}
            target="_blank"
            rel="noopener noreferrer"
            className="w-9 h-9 neu-button flex items-center justify-center text-hs-text hover:text-white"
            title="Open in New Tab"
          >
            <ExternalLink size={16} />
          </a>
        </div>
      </div>

      {/* Iframe Container - Max Depth Inset */}
      <div className="flex-1 min-h-0 bg-black relative">
        {isLoading && (
          <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-hs-bg">
            <div className="relative">
               <div className="w-24 h-24 neu-panel rounded-full flex items-center justify-center border-white/10">
                  <Loader2 className="w-10 h-10 text-hs-core animate-spin drop-shadow-[0_0_8px_rgba(79,152,255,0.5)]" />
               </div>
               <div className="absolute -inset-4 bg-hs-core/5 blur-2xl rounded-full animate-pulse"></div>
            </div>
            <div className="mt-8 text-center">
              <p className="text-xs font-mono font-bold text-white/40 uppercase tracking-[0.4em] animate-pulse">
                Preparing Studio
              </p>
              <p className="text-[10px] font-mono text-hs-text/30 mt-2 uppercase tracking-widest">
                Loading Secure Environment...
              </p>
            </div>
          </div>
        )}
        
        <div className="absolute inset-0 shadow-[inset_0_4px_20px_rgba(0,0,0,0.8)] pointer-events-none z-10"></div>
        <iframe
          src={iframeUrl}
          onLoad={() => setIsLoading(false)}
          className={`w-full h-full border-0 relative z-0 transition-opacity duration-1000 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
          title={app.name}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; microphone; camera; cross-origin-isolated; xr-spatial-tracking"
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals allow-downloads"
        />
      </div>
    </div>
  )
}
